<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import logoNeurals from '../../assets/img/logo_neurals_nuevo.png'

const currentYear = new Date().getFullYear()

// Índice de la pregunta abierta por defecto en el Acordeón (-1 para que empiecen cerradas)
const activeFaq = ref(0)

const faqs = [
  {
    question: '¿Cómo aseguran que el agente no invente precios o datos?',
    answer: 'Utilizamos Arquitectura RAG (Generación Aumentada por Recuperación). Esto significa que conectamos a los agentes cognitivos directamente con tus catálogos en tiempo real. El agente responde estrictamente basándose en tu catálogo, eliminando el riesgo de "alucinaciones" o información falsa.'
  },
  {
    question: '¿Puedo conectar más de un número de WhatsApp a la vez?',
    answer: 'Sí. Neurals.pro está diseñado con gestión multilínea. Puedes administrar múltiples instancias o números de WhatsApp desde un panel centralizado, asignando diferentes agentes según el departamento (Ventas, Soporte, Cobros).'
  },
  {
    question: '¿Cómo funciona la captura y cierre de leads?',
    answer: 'Durante una conversación natural, el agente está programado para identificar y extraer variables clave (como nombres, correos, requerimientos o presupuestos). Al finalizar la interacción, compila estos datos y envía un resumen ejecutivo estructurado a tu equipo de ventas o CRM.'
  },
  {
    question: '¿Tengo visibilidad de lo que hablan los agentes con mis clientes?',
    answer: 'Absolutamente. Tu panel de control incluye módulos de auditoría y supervisión. Puedes monitorear conversaciones completas en tiempo real, analizar métricas de interacción y tomar el control manual de la conversación.'
  }
]

const toggleFaq = (index) => {
  activeFaq.value = activeFaq.value === index ? -1 : index
}

// Botón directo a la plataforma (Product-Led Growth)
const openApp = () => {
  window.open('https://app.neurals.pro', '_blank')
}

// Lógica para animar la sección al hacer scroll
const sectionRef = ref(null)
const isVisible = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      isVisible.value = true
      observer.disconnect()
    }
  }, { threshold: 0.15 })

  if (sectionRef.value) {
    observer.observe(sectionRef.value)
  }
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <footer ref="sectionRef" class="bg-neural-bg text-neural-text pt-24 md:pt-32 pb-12 relative overflow-hidden border-t border-neural-border">

    <!-- EFECTOS DE FONDO: Clean Deep Tech Glow (Sin Cuadrícula) -->
    <div class="absolute inset-0 bg-neural-bg pointer-events-none"></div>
    <div class="absolute -top-24 left-1/2 -translate-x-1/2 w-[45rem] h-[45rem] bg-neural-primary/8 rounded-full blur-[160px] pointer-events-none animate-pulse"></div>

    <div class="container mx-auto px-6 lg:px-12 relative z-10">

      <!-- ================= 1. SECCIÓN: PREGUNTAS FRECUENTES (FAQ) ================= -->
      <div
        class="max-w-3xl mx-auto mb-24 transition-all duration-1000 transform"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
      >

        <!-- Cabecera FAQ -->
        <div class="text-center space-y-4 mb-12">

          <h2 class="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-neural-text">
            Preguntas Frecuentes
          </h2>
          <p class="text-base sm:text-lg text-neural-muted">
            Todo lo que necesitas saber sobre la integración de agentes cognitivos en tu empresa.
          </p>
        </div>

        <!-- Acordeón Interactivo -->
        <div class="space-y-4">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="rounded-2xl border transition-all duration-300 overflow-hidden"
            :class="activeFaq === index
              ? 'bg-neural-card border-neural-primary shadow-glow-primary'
              : 'bg-[#131316] border-neural-border hover:border-neural-muted/30'"
          >
            <!-- Cabecera de la pregunta -->
            <button
              @click="toggleFaq(index)"
              class="w-full py-5 px-6 sm:px-8 text-left flex items-center justify-between gap-4 focus:outline-none cursor-pointer"
            >
              <span class="text-base sm:text-lg font-semibold tracking-tight text-neural-text">
                {{ faq.question }}
              </span>
              <!-- Flecha rotativa -->
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 border"
                :class="activeFaq === index
                  ? 'rotate-180 bg-neural-primary text-white border-neural-primary'
                  : 'bg-neural-bg text-neural-muted border-neural-border'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <!-- Cuerpo de la respuesta -->
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-48 opacity-100"
              leave-active-class="transition-all duration-200 ease-in"
              leave-from-class="max-h-48 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-show="activeFaq === index" class="px-6 sm:px-8 pb-6 text-sm sm:text-base text-neural-muted leading-relaxed border-t border-neural-border pt-4">
                {{ faq.answer }}
              </div>
            </Transition>
          </div>
        </div>

      </div>

      <!-- ================= 2. SECCIÓN: CIERRE OPERATIVO Y BOTÓN CENTRAL (PLG) ================= -->
      <div
        class="max-w-4xl mx-auto bg-neural-card/80 backdrop-blur-xl border border-neural-border rounded-3xl p-8 sm:p-12 text-center space-y-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative overflow-hidden mb-16 transition-all duration-1000 delay-200 transform group"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
      >

        <!-- Brillos decorativos -->
        <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-neural-primary to-transparent"></div>
        <div class="absolute -right-20 -bottom-20 w-64 h-64 bg-neural-accent/10 rounded-full blur-[80px] pointer-events-none group-hover:bg-neural-accent/20 transition-colors duration-700"></div>

        <div class="space-y-3 relative z-10">
          <span class="text-xs font-mono font-bold text-neural-primary uppercase tracking-widest">
            ⚡ INICIA TU TRANSFORMACIÓN
          </span>
          <h3 class="text-3xl sm:text-4xl font-bold tracking-tight text-neural-text">
            ¿Listo para automatizar tus canales?
          </h3>
          <p class="text-neural-muted text-sm sm:text-base max-w-xl mx-auto">
            Accede a la plataforma, conecta tu catálogo y despliega tu propio agente cognitivo en minutos. Sin código ni configuraciones complejas.
          </p>
        </div>

        <!-- Botón Central Gigante directo a App -->
        <div class="pt-2 relative z-10">
          <button
            @click="openApp"
            class="w-full sm:w-auto px-10 py-4 rounded-xl bg-neural-primary hover:bg-neural-primary-hover text-white font-semibold text-base tracking-wide transition-all duration-300 shadow-glow-primary hover:shadow-[0_0_25px_rgba(99,102,241,0.6)] transform hover:-translate-y-0.5 flex items-center justify-center gap-3 mx-auto cursor-pointer"
          >
            <span>Iniciar 3 Días Gratis</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>

          <div class="flex items-center justify-center gap-4 mt-5 text-xs font-semibold text-neural-muted">
            <span class="flex items-center gap-1.5"><svg class="w-3.5 h-3.5 text-neural-online" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg> Sin tarjeta de crédito</span>
            <span class="w-1 h-1 rounded-full bg-neural-border hidden sm:block"></span>
            <span class="flex items-center gap-1.5"><svg class="w-3.5 h-3.5 text-neural-online" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg> Configuración inmediata</span>
          </div>
        </div>

      </div>

      <!-- ================= 3. BARRA INFERIOR DEL FOOTER ================= -->
      <div
        class="pt-8 border-t border-neural-border flex flex-col sm:flex-row items-center justify-between gap-6 text-xs text-neural-muted transition-all duration-1000 delay-300 transform"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >

        <!-- Identidad formal de la empresa -->
        <div class="flex items-center gap-3">
          <img :src="logoNeurals" alt="Neurals Logo" class="h-8 md:h-9 w-auto object-contain brightness-0 invert opacity-90 hover:opacity-100 transition-opacity">
          <span class="font-medium text-neural-muted/70 pl-3 border-l border-neural-border">
            © {{ currentYear }} Neurals. Todos los derechos reservados. <br class="sm:hidden"> Desarrollado por <a href="https://therealdevs.pro/" target="_blank" class="hover:text-neural-text transition-colors">The Real Devs Pro</a>
          </span>
        </div>

        <!-- Links Rápidos Formales -->
        <div class="flex flex-wrap items-center justify-center gap-6 font-medium">
          <a href="mailto:hola@neurals.pro" class="hover:text-neural-primary transition-colors flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            <span>hola@neurals.pro</span>
          </a>
          <a href="#capacidades" class="hover:text-neural-text transition-colors">Capacidades</a>
          <a href="#rag" class="hover:text-neural-text transition-colors">Tecnología RAG</a>
          <a href="#captura" class="hover:text-neural-text transition-colors">Captura Automática</a>
        </div>

      </div>

    </div>
  </footer>
</template>